import React, { useEffect, useRef, useState } from "react";

export interface GaugeProps {
    score: number;
    label?: string;
    avg100d: number;
    avg1y: number;
    sizeRem?: number;
}

const clamp = (v: number, min = 0, max = 100) =>
    Math.min(Math.max(v, min), max);

const getLabel = (score: number): string => {
    if (score >= 80) return "Extreme Greed";
    if (score >= 60) return "Greed";
    if (score >= 40) return "Neutral";
    if (score >= 20) return "Fear";
    return "Extreme Fear";
};

const ANIMATION_DURATION = 900;

const FearAndGreedGauge: React.FC<GaugeProps> = ({
    score,
    label,
    avg100d: avg100d,
    avg1y,
    sizeRem = 11.5,
}) => {
    const [animatedScore, setAnimatedScore] =
        useState(0);

    const prevScore = useRef(0);
    const frameRef = useRef<number | null>(null);

    const safeScore = clamp(score);

    /* ===== Animation ===== */
    useEffect(() => {
        const start = prevScore.current;
        const end = safeScore;
        const startTime = performance.now();

        const animate = (time: number) => {
            const progress = Math.min(
                (time - startTime) / ANIMATION_DURATION,
                1
            );

            const eased = 1 - Math.pow(1 - progress, 3);
            const value = start + (end - start) * eased;

            setAnimatedScore(value);

            if (progress < 1) {
                frameRef.current =
                    requestAnimationFrame(animate);
            } else {
                prevScore.current = end;
            }
        };

        frameRef.current =
            requestAnimationFrame(animate);

        return () => {
            if (frameRef.current)
                cancelAnimationFrame(frameRef.current);
        };
    }, [safeScore]);

    /* ===== Geometry ===== */
    const sizePx = sizeRem * 16;
    const radius = sizePx / 2 - 24;
    const circumference = Math.PI * radius;

    const angle =
        (animatedScore / 100) * 180 - 180;

    const displayLabel =
        label ?? getLabel(animatedScore);

    /* ===== Segments ===== */
    const segments = [
        "#FFE8D1",
        "#FFD2A6",
        "#FFB870",
        "#FFA94D",
        "#F7931A",
    ];
    const segmentLength = circumference / 5;

    /* ===== Arrow geometry (closer to center) ===== */
    const arrowDistance = radius - 15;
    const rad = (angle * Math.PI) / 180;
    const arrowX = Math.cos(rad) * arrowDistance;
    const arrowY = Math.sin(rad) * arrowDistance;

    return (
        <div
            style={{
                width: `${sizePx}px`,
                textAlign: "center",
                fontFamily: "Arial, sans-serif",
                color: "#000",
            }}
        >

            <svg
                width={`${sizePx}px`}
                height={`${sizePx / 1.4}px`}
                viewBox={`0 0 ${sizePx} ${sizePx / 2}`}
            >

                <g
                    transform={`translate(${sizePx / 2}, ${sizePx / 2.2
                        })`}
                >
                    {/* Title */}
                    <text
                        y={-radius - sizePx * 0.11}
                        textAnchor="middle"
                        fontSize={sizePx * 0.1}
                        fontWeight="600"
                        fill="#000"
                    >
                        Fear and Greed
                    </text>

                    {/* Segmented arc */}
                    {segments.map((color, i) => (
                        <path
                            key={i}
                            d={`
                M ${-radius} 0
                A ${radius} ${radius} 0 0 1 ${radius} 0
              `}
                            fill="none"
                            stroke={color}
                            strokeWidth={10}
                            strokeDasharray={`${segmentLength} ${circumference}`}
                            strokeDashoffset={
                                -segmentLength * i
                            }
                            strokeLinecap="butt"
                        />
                    ))}

                    {/* Floating Arrow closer to center */}
                    <g
                        transform={`translate(${arrowX}, ${arrowY}) rotate(${angle})`}
                        style={{ opacity: 0.85 }}
                    >
                        <polygon
                            points="0,-6 14,0 0,6"
                            fill="#000"
                        />
                    </g>

                    {/* Score */}
                    <text
                        y={-10}
                        textAnchor="middle"
                        fontSize={sizePx * 0.12}
                        fontWeight="600"
                        fill="#000"
                    >
                        {Math.round(animatedScore)}
                    </text>

                    {/* Larger classification label */}
                    <text
                        y={sizePx * 0.12}
                        textAnchor="middle"
                        fontWeight="400"
                        fontStyle="italic"
                        fill="#475569"
                    >
                        {displayLabel}
                    </text>

                </g>
            </svg>

            <div
                style={{
                    width: "42%",
                    height: "1px",
                    margin: "0.1rem auto 0.25rem",
                    backgroundColor: "rgba(247, 147, 26, 0.65)",
                }}
            />

            {/* Stats */}
            <div
                style={{
                    marginTop: "0.3rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                }}
            >
                <div>
                    <span>100 Day Average: </span>
                    <span style={{ color: "#000" }}>
                        <strong>{avg100d.toFixed(1)}</strong>
                    </span>
                    <br />
                    <span>1 Year Average: </span>
                    <span style={{ color: "#000" }}>
                        <strong>{avg1y.toFixed(1)}</strong>
                    </span>
                </div>
            </div>

        </div>
    );
};

export default React.memo(FearAndGreedGauge);
